import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet } from 'react-router';
import { useTranslation } from 'react-i18next';
import { LogOut, Menu as IconeMenu, X } from 'lucide-react';
import type { Eu } from '../api';
import { useLogout, useTrocarWorkspace } from '../auth/sessao';
import { menuVisivel } from './menu';
import { AlternarTema, SeletorIdioma, aplicarAparencia } from './Preferencias';
import { MarcaCortex } from './Marca';
import { Tour } from './Tour';
import { MfaSalaHost } from '../mfa/MfaSalaHost';
import '../mfa/i18n';

/** F5: sidebar de 256px, recolhível no mobile, item ativo com fundo de acento suave, sem numeração nem badge. */
export function Shell({ eu }: { eu: Eu }) {
  const { t } = useTranslation();
  const { t: tMfa } = useTranslation('mfa');
  const [aberta, setAberta] = useState(false);
  const botaoAbrir = useRef<HTMLButtonElement>(null);
  const refMenu = useRef<HTMLElement>(null);
  const refConteudo = useRef<HTMLDivElement>(null);
  // VA-03: ao fechar o menu no mobile, o foco volta para o botão que o abriu.
  const fechar = () => {
    // O conteúdo deixa de ser inerte antes do foco voltar (botão inerte não recebe foco).
    refConteudo.current?.removeAttribute('inert');
    setAberta(false);
    botaoAbrir.current?.focus();
  };
  const logout = useLogout();
  const trocar = useTrocarWorkspace();
  const itens = menuVisivel(eu.modulos ?? [], eu.permissoes);
  // DS0.1: aplica o tema resolvido (usuário → workspace → sistema → escuro); o alternador acompanha o tema efetivo sozinho.
  useEffect(() => void aplicarAparencia(eu), [eu]);
  // VA4-03 (rodada 4): no mobile o menu aberto é modal. O foco entra no menu, o Tab circula dentro dele, o conteúdo
  // atrás fica inerte e Esc fecha devolvendo o foco ao botão que abriu.
  useEffect(() => {
    if (!aberta) return;
    const menu = refMenu.current;
    const conteudo = refConteudo.current;
    conteudo?.setAttribute('inert', '');
    menu?.querySelector<HTMLElement>('a[href], button')?.focus();
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        fechar();
        return;
      }
      if (e.key !== 'Tab' || !menu) return;
      const lista = [...menu.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), select, input')].filter((x) => x.offsetParent !== null);
      if (!lista.length) return;
      const primeiro = lista[0]!;
      const ultimo = lista[lista.length - 1]!;
      const atual = document.activeElement;
      if (e.shiftKey && (atual === primeiro || !menu.contains(atual))) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && (atual === ultimo || !menu.contains(atual))) {
        e.preventDefault();
        primeiro.focus();
      }
    };
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('keydown', tecla);
      conteudo?.removeAttribute('inert');
    };
  }, [aberta]);

  return (
    <div className="flex altura-janela">
      <aside
        ref={refMenu}
        data-tour="sidebar"
        className={`fixed inset-y-0 left-0 z-30 flex w-64 flex-col border-r border-borda bg-sup-1 transition-transform lg:visible lg:sticky lg:top-0 altura-lateral lg:translate-x-0 ${aberta ? 'visible translate-x-0' : 'invisible -translate-x-full'}`}
      >
        <div className="flex items-center justify-between gap-3 px-5 pt-5">
          <MarcaCortex />
          <button type="button" onClick={fechar} aria-label={t('shell.fecharMenu')} className="text-tinta-3 hover:text-tinta-1 lg:hidden">
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <label data-tour="workspace" className="mx-5 mt-5 flex flex-col gap-1.5">
          <span className="kicker">{t('shell.workspace')}</span>
          <select
            value={eu.workspace_id}
            onChange={(e) => trocar.mutate(e.target.value)}
            className="h-9 rounded-lg border border-borda bg-sup-2 px-2 text-sm text-tinta-1 outline-none focus:border-acento"
          >
            {eu.workspaces.map((w) => (
              <option key={w.workspace_id} value={w.workspace_id}>
                {w.workspace_nome}
              </option>
            ))}
          </select>
        </label>

        <nav className="mt-6 flex flex-1 flex-col gap-0.5 overflow-y-auto px-3" aria-label="Principal">
          {itens.map((m) => (
            <NavLink
              key={m.chave}
              data-tour={m.chave === 'admin' ? 'admin' : undefined}
              to={m.rota}
              end={m.rota === '/'}
              onClick={() => setAberta(false)}
              className={({ isActive }) =>
                `flex h-10 items-center gap-3 rounded-lg px-3 text-sm ${isActive ? 'bg-acento-suave font-medium text-tinta-1' : 'text-tinta-2 hover:bg-sup-2 hover:text-tinta-1'}`
              }
            >
              <m.icone size={17} strokeWidth={1.75} />
              {t(`menu.${m.chave}`)}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-borda p-4">
          <div className="mb-3 flex items-center justify-between">
            <SeletorIdioma />
            <AlternarTema usuarioId={eu.usuario.id} />
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <NavLink to="/conta" onClick={() => setAberta(false)} className="block truncate text-sm font-medium hover:text-acento-texto focus-visible:outline focus-visible:outline-2 focus-visible:outline-foco" title={tMfa('conta')}>{eu.usuario.nome}</NavLink>
              <p className="truncate text-xs text-tinta-3">{eu.papel_fabrica ? t(`papel.${eu.papel_fabrica}`) : eu.usuario.papel}</p>
              <NavLink to="/conta" onClick={() => setAberta(false)} className="mt-1 block text-xs text-acento-texto hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-foco">{tMfa('conta')}</NavLink>
            </div>
            <button
              type="button"
              onClick={() => logout.mutate()}
              title={t('shell.sair')}
              aria-label={t('shell.sair')}
              className="grid size-8 shrink-0 place-items-center rounded-lg text-tinta-3 hover:bg-sup-2 hover:text-tinta-1"
            >
              <LogOut size={16} strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </aside>

      {aberta && <button type="button" aria-label={t('shell.fecharMenu')} onClick={fechar} className="fixed inset-0 z-20 bg-fundo/60 lg:hidden" />}

      <div ref={refConteudo} className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center gap-3 border-b border-borda px-4 lg:hidden">
          <button ref={botaoAbrir} type="button" onClick={() => setAberta(true)} aria-label={t('shell.abrirMenu')} aria-expanded={aberta} className="text-tinta-2">
            <IconeMenu size={20} strokeWidth={1.75} />
          </button>
          <MarcaCortex />
        </header>
        <main className="min-w-0 flex-1">
          <Outlet context={eu} />
        </main>
        <Tour eu={eu} />
        <MfaSalaHost eu={eu} />
      </div>
    </div>
  );
}
